<template>
  <ion-page>
    <ion-content
      :fullscreen="true"
      class="h-[100svh] w-screen text-center text-grey-100 font-aktiv-grotesk"
    >
      <p
        v-if="!error"
        class="w-full h-full px-[24px] flex justify-center items-center m-auto text-[32px] leading-[40px] font-aktiv-grotesk-medium"
      >
        🔒 Logging you in...
      </p>
      <div
        v-if="error"
        class="w-full h-full px-[24px] flex flex-col justify-center items-center m-auto"
      >
        <p class="text-[32px] leading-[40px]">Something went wrong</p>
        <p class="text-[20px] leading-[28px] font-aktiv-grotesk mt-[16px]">
          We couldn't complete your login.
        </p>
        <button
          class="bg-[#00A9E0] text-white px-[40px] py-[16px] text-[20px] leading-[24px] rounded-[40px] font-aktiv-grotesk-medium mt-[24px]"
          @click="goToHome()"
        >
          Try again
        </button>
      </div>
    </ion-content>
  </ion-page>
</template>

<script>
import { IonContent, IonPage } from "@ionic/vue";
import { Browser } from "@capacitor/browser";
import { defineComponent } from "vue";
import { useAuth0 } from "@auth0/auth0-vue";
import { getLoginPathObj } from "../../utils/redirect-uri";
import { useRoute, useRouter } from "vue-router";
import { isPlatform } from "@ionic/vue";
import { initLoginTracking } from "@/utils/login-tracking.js";
import { datadogLogs } from "@datadog/browser-logs";

export default defineComponent({
  name: "Callback",
  components: {
    IonContent,
    IonPage,
  },

  setup() {
    const { isAuthenticated, user, handleRedirectCallback } = useAuth0();
    const route = useRoute();
    const router = useRouter();

    return {
      isAuthenticated,
      user,
      route,
      router,
      handleRedirectCallback,
    };
  },
  data() {
    return {
      error: false,
    };
  },
  async mounted() {
    const isNative = isPlatform("ios") || isPlatform("android");
    const isNativeBrowser = isPlatform("mobileweb");

    // For native apps, App.vue handles the callback via appUrlOpen
    // This page should only handle web callbacks
    if (isNative && !isNativeBrowser) {
      console.log("Native app detected - callback handled by App.vue");
      try {
        await Browser.close();
      } catch (error) {
        console.log("Error closing browser:", error);
      }
      this.router.push("/home");
      return;
    }

    // Web callback handling
    try {
      initLoginTracking(this.user ? this.user.sub : "");
    } catch {
      console.error("Error init logging tracking");
    }

    // Handle access denied errors
    if (this.route.query.error === "access_denied") {
      const errorDescription = this.route.query.error_description;
      if (errorDescription === "access denied") {
        this.router.push("/access-denied");
        return;
      }
    }

    // Retry logic for web callbacks
    const maxRetries = 3;
    const baseDelay = 5000;

    const attemptCallback = async (attempt = 1) => {
      try {
        console.log(`Callback attempt ${attempt}/${maxRetries}`);

        const appState = await this.handleRedirectCallback();

        // Navigate after successful callback
        if (appState && appState.fullPathObj) {
          this.router.push({
            path: appState.fullPathObj.path,
            query: appState.fullPathObj.query,
            hash: appState.fullPathObj.hash,
          });
        } else {
          // Check for stored login path
          const loginPathObj = getLoginPathObj();
          if (loginPathObj && loginPathObj.path !== "/") {
            this.router.push({
              path: loginPathObj.path,
              query: loginPathObj.query,
              hash: loginPathObj.hash,
            });
          } else {
            this.router.push("/home");
          }
        }
        return true; // Success
      } catch (err) {
        const userId = this.user ? this.user.sub : "";
        console.log(`Attempt ${attempt} failed:`, err.name, err.message);

        // Log to Datadog if configured
        if (process.env.VUE_APP_DATADOG_CLIENT_TOKEN) {
          const message = `User (auth0Id: ${userId}) login attempt ${attempt} failed:`;
          const data = {
            auth0Id: userId,
            logType: "login",
            errMesage: err.message,
          };
          datadogLogs.logger.warn(message, data);
        }

        // Handle Invalid state error
        if (err.message === "Invalid state") {
          console.log("Invalid state error - forwarding after login");
          const loginPathObj = getLoginPathObj();
          if (loginPathObj && loginPathObj.path !== "/") {
            this.router.push({
              path: loginPathObj.path,
              query: loginPathObj.query,
              hash: loginPathObj.hash,
            });
          } else {
            this.router.push("/home");
          }
          return true; // Handle this as success since we're forwarding
        }

        // Check if we should retry
        if (attempt >= maxRetries) {
          const message = `User (auth0Id: ${userId}): Max retries reached for handleRedirectCallback`;
          if (process.env.VUE_APP_DATADOG_CLIENT_TOKEN) {
            datadogLogs.logger.error(message, {
              auth0Id: userId,
              logType: "login",
            });
          }
          throw err; // Re-throw after max retries
        }

        // Exponential backoff: wait longer between retries
        const delay = baseDelay * Math.pow(2, attempt - 1);
        console.log(`Retrying in ${delay}ms...`);
        await new Promise((resolve) => setTimeout(resolve, delay));

        return attemptCallback(attempt + 1);
      }
    };

    try {
      await attemptCallback();
    } catch (finalErr) {
      console.error("All callback attempts failed:", finalErr);

      // If user is authenticated despite error, proceed
      if (this.isAuthenticated) {
        const loginPathObj = getLoginPathObj();
        if (loginPathObj && loginPathObj.path !== "/") {
          this.router.push({
            path: loginPathObj.path,
            query: loginPathObj.query,
            hash: loginPathObj.hash,
          });
        } else {
          this.router.push("/home");
        }
      } else {
        this.error = true;
      }
    }

    // Handle if user refreshes when stuck in callback
    if (this.isAuthenticated) {
      console.log("User already authenticated, forwarding...");
      const loginPathObj = getLoginPathObj();
      if (loginPathObj && loginPathObj.path !== "/") {
        this.router.push({
          path: loginPathObj.path,
          query: loginPathObj.query,
          hash: loginPathObj.hash,
        });
      } else {
        this.router.push("/home");
      }
    }
  },
  methods: {
    goToHome() {
      this.$router.push("/home");
    },
  },
});
</script>

<style scoped>
#container {
  text-align: center;

  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
}

#container strong {
  font-size: 20px;
  line-height: 26px;
}

#container p {
  font-size: 16px;
  line-height: 22px;

  color: #8c8c8c;

  margin: 0;
}

#container a {
  text-decoration: none;
}
</style>
